/**
 * Data API を利用してコンテンツタイプを複製します。
 * 同一サイトまたは別サイトへの複製が可能です。
 *
 * Updated: 2026/10/07
 *
 * @requires mtappVars bootstrap.Modal
 *
 * 使用例（ボタン追加モード）:
 * ```
 * mtapp.duplicateContentType({
 *     addButton: true
 * });
 * ```
 *
 * 使用例（ダイアログモード）:
 * ```
 * mtapp.duplicateContentType({
 *     contentTypeId: 123
 * });
 * ```
 *
 * 使用例（プログラマティックモード）:
 * ```
 * mtapp.duplicateContentType({
 *     contentTypeId: 123,
 *     targetSiteId: 456,
 *     newName: '新しいコンテンツタイプ名'
 * });
 * ```
 *
 * @param {Object} options
 * @param {boolean} [options.addButton=false] - true の場合、コンテンツタイプ編集画面に複製ボタンを追加
 * @param {number} [options.contentTypeId] - 複製元のコンテンツタイプID（addButton時は自動取得）
 * @param {number} [options.sourceSiteId] - 複製元サイトID（デフォルト: 現在のサイト）
 * @param {number} [options.targetSiteId] - 複製先サイトID（デフォルト: 現在のサイト）
 * @param {string} [options.newName] - 新しいコンテンツタイプ名（デフォルト: 元の名前 + " (コピー)"）
 * @returns {Promise<Object|null>|void} 複製されたコンテンツタイプ情報（ダイアログをキャンセルした場合は null、addButton時・ダイアログ表示中の再呼び出し時はundefined）
 */
import { loadingImage } from './loadingImage.js';
import { modalMsg } from './modalMsg.js';
import { getDataApiUrl } from './getDataApiUrl.js';
import { escapeHtml } from './escapeHtml.js';

// ダイアログを開いている（開く準備中を含む）かどうか。開くアニメーション中の Enter キーなどで
// 二重に呼ばれると同じ ID のダイアログが重なり、背景レイヤーが残って操作できなくなるため。
let isDialogOpen = false;

/**
 * ダイアログHTMLを生成
 * @param {Array<{id: number, name: string}>} sites - サイト一覧
 * @param {number} currentSiteId - 現在のサイトID
 * @param {string} defaultName - デフォルトの名前
 * @returns {string} HTML文字列
 */
function createDialogHTML(sites, currentSiteId, defaultName) {
    const siteOptions = sites.map(site => {
        const selected = site.id === currentSiteId ? 'selected' : '';
        return `<option value="${escapeHtml(site.id)}" ${selected}>${escapeHtml(site.name)}</option>`;
    }).join('');

    return `
        <div id="mtapp-modal-duplicate-content-type" class="modal fade" tabindex="-1" aria-labelledby="mtapp-modal-duplicate-content-type-title" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 id="mtapp-modal-duplicate-content-type-title" class="modal-title">コンテンツタイプを複製</h4>
                        <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="mb-3">
                            <label for="mtapp-duplicate-ct-target-site" class="form-label">複製先サイト</label>
                            <select id="mtapp-duplicate-ct-target-site" class="form-select">
                                ${siteOptions}
                            </select>
                        </div>
                        <div class="mb-3">
                            <label for="mtapp-duplicate-ct-new-name" class="form-label">新しいコンテンツタイプ名</label>
                            <input type="text" id="mtapp-duplicate-ct-new-name" class="form-control" value="${escapeHtml(defaultName)}">
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-default" data-bs-dismiss="modal">キャンセル</button>
                        <button type="button" id="mtapp-duplicate-ct-submit" class="btn btn-primary">複製</button>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * コンテンツタイプを複製するメイン処理
 * @param {Object} params
 * @param {number} params.contentTypeId - 複製元のコンテンツタイプID
 * @param {number} params.sourceSiteId - 複製元サイトID
 * @param {number} params.targetSiteId - 複製先サイトID
 * @param {string} params.newName - 新しいコンテンツタイプ名
 * @param {string} params.dataApiUrl - Data API URL
 * @param {string} params.accessToken - アクセストークン
 * @param {string} params.adminScript - 管理画面スクリプトURL
 * @returns {Promise<Object>} 複製されたコンテンツタイプ情報
 */
async function executeDuplication(params) {
    const {
        contentTypeId,
        sourceSiteId,
        targetSiteId,
        newName,
        dataApiUrl,
        accessToken,
        adminScript
    } = params;

    const headers = {
        'Content-Type': 'application/json',
        'X-MT-Authorization': `MTAuth accessToken=${accessToken}`
    };

    try {
        loadingImage('show');

        // 1. 複製元コンテンツタイプ情報を取得
        const ctResponse = await fetch(
            `${dataApiUrl}/sites/${sourceSiteId}/contentTypes/${contentTypeId}`,
            { method: 'GET', headers }
        );

        if (!ctResponse.ok) {
            if (ctResponse.status === 404) {
                throw new Error('コンテンツタイプが見つかりません。');
            } else if (ctResponse.status === 403) {
                throw new Error('コンテンツタイプを取得する権限がありません。');
            }
            throw new Error(`コンテンツタイプの取得に失敗しました (HTTP ${ctResponse.status})`);
        }

        const contentType = await ctResponse.json();

        // 2. 複製元コンテンツフィールド一覧を取得
        const fieldsResponse = await fetch(
            `${dataApiUrl}/sites/${sourceSiteId}/contentTypes/${contentTypeId}/fields?limit=1000`,
            { method: 'GET', headers }
        );

        if (!fieldsResponse.ok) {
            throw new Error(`コンテンツフィールドの取得に失敗しました (HTTP ${fieldsResponse.status})`);
        }

        const fieldsData = await fieldsResponse.json();
        const sourceFields = fieldsData.items || [];

        // 3. 複製先サイトに新しいコンテンツタイプを作成
        const newContentType = {
            name: newName,
            description: contentType.description || '',
            userDisplayOption: contentType.userDisplayOption || false,
            dataLabel: contentType.dataLabel || ''
        };

        const createCtFormData = new FormData();
        createCtFormData.append('content_type', JSON.stringify(newContentType));

        const createCtResponse = await fetch(
            `${dataApiUrl}/sites/${targetSiteId}/contentTypes`,
            {
                method: 'POST',
                headers: {
                    'X-MT-Authorization': `MTAuth accessToken=${accessToken}`
                },
                body: createCtFormData
            }
        );

        if (!createCtResponse.ok) {
            if (createCtResponse.status === 403) {
                throw new Error('コンテンツタイプを作成する権限がありません。');
            }
            const errorData = await createCtResponse.json().catch(() => ({}));
            throw new Error(errorData.error?.message || `コンテンツタイプの作成に失敗しました (HTTP ${createCtResponse.status})`);
        }

        const newCt = await createCtResponse.json();
        const newContentTypeId = newCt.id;

        // 4. 各コンテンツフィールドを順番に作成
        const newFieldIds = [];

        for (const field of sourceFields) {
            const newField = {
                type: field.type,
                label: field.label || '',
                description: field.description || '',
                options: field.options || {}
            };

            const createFieldFormData = new FormData();
            createFieldFormData.append('content_field', JSON.stringify(newField));

            const createFieldResponse = await fetch(
                `${dataApiUrl}/sites/${targetSiteId}/contentTypes/${newContentTypeId}/fields`,
                {
                    method: 'POST',
                    headers: {
                        'X-MT-Authorization': `MTAuth accessToken=${accessToken}`
                    },
                    body: createFieldFormData
                }
            );

            if (!createFieldResponse.ok) {
                // フィールド作成に失敗した場合、作成したコンテンツタイプを削除
                console.error(`フィールド "${field.label}" の作成に失敗しました`);
                await deleteContentType(dataApiUrl, targetSiteId, newContentTypeId, accessToken);
                throw new Error(`コンテンツフィールド "${field.label}" の作成に失敗しました`);
            }

            const newFieldData = await createFieldResponse.json();
            newFieldIds.push({ id: newFieldData.id });
        }

        // 5. フィールドの順序を設定（フィールドが2つ以上ある場合）
        if (newFieldIds.length >= 2) {
            const permutateFormData = new FormData();
            permutateFormData.append('content_fields', JSON.stringify(newFieldIds));

            const permutateResponse = await fetch(
                `${dataApiUrl}/sites/${targetSiteId}/contentTypes/${newContentTypeId}/fields/permutate`,
                {
                    method: 'POST',
                    headers: {
                        'X-MT-Authorization': `MTAuth accessToken=${accessToken}`
                    },
                    body: permutateFormData
                }
            );

            if (!permutateResponse.ok) {
                console.warn('フィールドの順序設定に失敗しましたが、コンテンツタイプは作成されました。');
            }
        }

        loadingImage('hide');

        // 6. 編集画面に遷移
        const redirectUrl = `${adminScript}?__mode=view&_type=content_type&blog_id=${targetSiteId}&id=${newContentTypeId}`;
        location.href = redirectUrl;

        return newCt;

    } catch (error) {
        loadingImage('hide');
        throw error;
    }
}

/**
 * コンテンツタイプを削除（ロールバック用）
 * @param {string} dataApiUrl - Data API URL
 * @param {number} siteId - サイトID
 * @param {number} contentTypeId - コンテンツタイプID
 * @param {string} accessToken - アクセストークン
 */
async function deleteContentType(dataApiUrl, siteId, contentTypeId, accessToken) {
    try {
        await fetch(
            `${dataApiUrl}/sites/${siteId}/contentTypes/${contentTypeId}`,
            {
                method: 'DELETE',
                headers: {
                    'X-MT-Authorization': `MTAuth accessToken=${accessToken}`
                }
            }
        );
    } catch (e) {
        console.error('ロールバック時のコンテンツタイプ削除に失敗しました:', e);
    }
}

/**
 * コンテンツタイプを複製
 * @param {Object} options - オプション
 * @returns {Promise<Object|null>|void} 複製されたコンテンツタイプ情報（キャンセル時は null、addButton時・ダイアログ表示中の再呼び出し時はundefined）
 */
export async function duplicateContentType(options = {}) {
    // @ts-ignore
    const mtappVars = window.mtappVars;
    const dataApiUrl = getDataApiUrl();

    const {
        addButton = false,
        contentTypeId,
        sourceSiteId = mtappVars.blog_id,
        targetSiteId,
        newName
    } = options;

    // addButton モード: コンテンツタイプ編集画面にボタンを追加
    if (addButton) {
        // 画面チェック: コンテンツタイプ編集画面のみ
        if (mtappVars.mode !== 'view' || mtappVars.type !== 'content_type') {
            return;
        }

        // 重複適用防止チェック
        if (!mtappVars.__system) {
            mtappVars.__system = {};
        }
        if (mtappVars.__system.hasDuplicateContentTypeButton) {
            return;
        }
        mtappVars.__system.hasDuplicateContentTypeButton = true;

        // ボタンの挿入先を取得
        const saveButton = document.querySelector('[data-is="content-fields"] .btn-primary');
        if (!saveButton) {
            return;
        }

        // 複製ボタンを作成
        const duplicateButton = document.createElement('button');
        duplicateButton.type = 'button';
        duplicateButton.id = 'mtapp-duplicate-content-type-button';
        duplicateButton.className = 'btn btn-warning text-white ms-2';
        duplicateButton.textContent = '複製';

        // ボタンを挿入
        saveButton.insertAdjacentElement('afterend', duplicateButton);

        // クリックイベントを設定
        duplicateButton.addEventListener('click', function() {
            duplicateContentType({
                contentTypeId: mtappVars.id
            });
        });

        return;
    }

    // contentTypeId が指定されていない場合はエラー
    if (!contentTypeId) {
        throw new Error('contentTypeId は必須です。');
    }

    const accessToken = mtappVars.accessToken || '';
    const adminScript = mtappVars.adminScript || location.href.replace(/\?.*/, '');

    // プログラマティックモード（targetSiteId と newName が指定されている場合）
    if (targetSiteId && newName) {
        return executeDuplication({
            contentTypeId,
            sourceSiteId,
            targetSiteId,
            newName,
            dataApiUrl,
            accessToken,
            adminScript
        });
    }

    // ダイアログモード
    if (isDialogOpen) {
        return;
    }
    isDialogOpen = true;
    try {
        loadingImage('show');

        // 複製元コンテンツタイプの名前を取得
        const ctResponse = await fetch(
            `${dataApiUrl}/sites/${sourceSiteId}/contentTypes/${contentTypeId}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-MT-Authorization': `MTAuth accessToken=${accessToken}`
                }
            }
        );

        if (!ctResponse.ok) {
            throw new Error('コンテンツタイプの取得に失敗しました。');
        }

        const contentType = await ctResponse.json();
        const defaultNewName = `${contentType.name} (コピー)`;

        // サイト一覧を取得（permitted_sites を使用、または Data API で取得）
        let sites = [];

        if (mtappVars.permitted_sites && mtappVars.permitted_sites.length > 0) {
            sites = mtappVars.permitted_sites.map(site => ({
                id: Number(site.id),
                name: site.name
            }));
        } else {
            // Data API でサイト一覧を取得
            const sitesResponse = await fetch(
                `${dataApiUrl}/sites?limit=1000`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-MT-Authorization': `MTAuth accessToken=${accessToken}`
                    }
                }
            );

            if (sitesResponse.ok) {
                const sitesData = await sitesResponse.json();
                sites = (sitesData.items || []).map(site => ({
                    id: site.id,
                    name: site.name
                }));
            }
        }

        // サイトが見つからない場合は現在のサイトのみ
        if (sites.length === 0) {
            sites = [{
                id: mtappVars.blog_id,
                name: mtappVars.blog_name || 'Current Site'
            }];
        }

        loadingImage('hide');

        // ダイアログを表示
        const dialogHTML = createDialogHTML(sites, mtappVars.blog_id, defaultNewName);
        document.body.insertAdjacentHTML('beforeend', dialogHTML);

        const modalElement = document.getElementById('mtapp-modal-duplicate-content-type');
        // @ts-ignore
        const modal = new window.bootstrap.Modal(modalElement);
        const targetSiteSelect = /** @type {HTMLSelectElement} */ (document.getElementById('mtapp-duplicate-ct-target-site'));
        const newNameInput = /** @type {HTMLInputElement} */ (document.getElementById('mtapp-duplicate-ct-new-name'));
        const submitButton = document.getElementById('mtapp-duplicate-ct-submit');

        // Promise を返してダイアログの結果を待つ
        return new Promise((resolve, reject) => {
            let submitted = false;
            // 複製ボタンクリック時の処理
            const handleSubmit = async () => {
                const selectedTargetSiteId = Number(targetSiteSelect.value);
                const enteredNewName = newNameInput.value.trim();

                if (!enteredNewName) {
                    modalMsg({
                        type: 'error',
                        title: 'エラー',
                        content: 'コンテンツタイプ名を入力してください。'
                    });
                    return;
                }

                submitted = true;
                modal.hide();

                try {
                    const result = await executeDuplication({
                        contentTypeId,
                        sourceSiteId,
                        targetSiteId: selectedTargetSiteId,
                        newName: enteredNewName,
                        dataApiUrl,
                        accessToken,
                        adminScript
                    });
                    resolve(result);
                } catch (error) {
                    modalMsg({
                        type: 'error',
                        title: 'エラー',
                        content: error.message || 'コンテンツタイプの複製に失敗しました。'
                    });
                    reject(error);
                }
            };

            submitButton.addEventListener('click', handleSubmit);

            // モーダルが閉じられたときの処理
            modalElement.addEventListener('hidden.bs.modal', function () {
                document.body.removeChild(modalElement);
                submitButton.removeEventListener('click', handleSubmit);
                isDialogOpen = false;
                // キャンセルで閉じたときも Promise を未解決のまま残さない
                if (!submitted) {
                    resolve(null);
                }
            });

            modal.show();
        });

    } catch (error) {
        isDialogOpen = false;
        loadingImage('hide');
        modalMsg({
            type: 'error',
            title: 'エラー',
            content: error.message || 'コンテンツタイプの複製に失敗しました。'
        });
        throw error;
    }
}
